/* Blocos compartilhados: card, tiles, estados de carregamento/vazio/erro,
 * e o quadro que envolve todo grafico.
 *
 * O ChartFrame carrega a promessa de acessibilidade: todo grafico ganha titulo,
 * uma alternativa em tabela dentro de <details>, e um link "como ler" para a
 * secao correspondente do guia. */

import { type ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../lib/api";
import { cell, compact, pct } from "../lib/format";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/** Markdown cru do banco. Sem HTML bruto -- o CSP nao deixaria de qualquer jeito. */
export function Markdown({ text }: { text: string }) {
  return (
    <div className="prose">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </div>
  );
}

export function Card({ title, sub, right, children, id }: {
  title?: ReactNode; sub?: ReactNode; right?: ReactNode; children: ReactNode; id?: string;
}) {
  return (
    <section className="card" id={id}>
      {(title || right) && (
        <header>
          {title && <h2>{title}</h2>}
          {sub && <p className="sub">{sub}</p>}
          {right && <div className="right">{right}</div>}
        </header>
      )}
      <div className="body">{children}</div>
    </section>
  );
}

export function Tiles({ items }: {
  items: { key: string; label: string; value: number | null; unit: string;
           flow?: string; detail?: string }[];
}) {
  const render = (value: number | null, unit: string) => {
    if (value === null || value === undefined) return "–";
    if (unit === "BRL") return `R$ ${compact(value)}`;
    if (unit === "ratio") return pct(value);
    if (unit === "%") return `${value}%`;
    return cell(value) === "–" ? String(value) : value.toLocaleString("pt-BR");
  };
  return (
    <div className="tiles">
      {items.map((t) => (
        <div className="tile" key={t.key} data-flow={t.flow}>
          <div className="k">{t.label}</div>
          <div className="v">{render(t.value, t.unit)}</div>
          {t.detail && <div className="d">{t.detail}</div>}
        </div>
      ))}
    </div>
  );
}

/** Esqueleto com a forma do conteudo final. Spinner nao diz o que vem. */
export function Skeleton({ rows = 6, height = 22 }: { rows?: number; height?: number }) {
  return (
    <div style={{ display: "grid", gap: 8 }} aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton"
             style={{ height, width: `${100 - (i % 3) * 7}%` }} />
      ))}
      <span className="sr-only" style={{ position: "absolute", left: -9999 }}>
        Carregando…
      </span>
    </div>
  );
}

export function SkeletonGrid({ count = 9 }: { count?: number }) {
  return (
    <div className="sparkgrid">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 108 }} />
      ))}
    </div>
  );
}

export function Empty({ children, onClear }: { children: ReactNode; onClear?: () => void }) {
  return (
    <div className="empty">
      <p style={{ margin: 0 }}>{children}</p>
      {onClear && (
        <button className="btn" style={{ marginTop: 12 }} onClick={onClear}>
          Limpar filtros
        </button>
      )}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const api = error instanceof ApiError ? error : null;
  return (
    <div className="error" role="alert">
      <p style={{ margin: 0, fontWeight: 500 }}>
        {api?.message ?? "Não foi possível carregar os dados."}
      </p>
      {api && <code>{api.code} · {api.endpoint}</code>}
      {onRetry && (
        <button className="btn" style={{ marginTop: 12 }} onClick={onRetry}>
          Tentar de novo
        </button>
      )}
    </div>
  );
}

/** Envolve um grafico: titulo, alternativa em tabela, e o link para o guia. */
export function ChartFrame({ title, sub, guide, tableHeader, tableRows, right, children }: {
  title: string; sub?: ReactNode; guide?: string;
  tableHeader?: string[]; tableRows?: (string | number)[][];
  right?: ReactNode; children: ReactNode;
}) {
  return (
    <Card
      title={title}
      sub={sub}
      right={
        <>
          {right}
          {guide && (
            <Link className="btn" to={`/guia#${guide}`} title="Como ler este gráfico">
              como ler
            </Link>
          )}
        </>
      }
    >
      {children}
      {tableHeader && tableRows && (
        <details style={{ marginTop: 14 }}>
          <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--ink-3)" }}>
            ver como tabela ({tableRows.length} linhas)
          </summary>
          <div style={{ overflowX: "auto", marginTop: 8 }}>
            <table>
              <thead>
                <tr>{tableHeader.map((h) => <th key={h} scope="col">{h}</th>)}</tr>
              </thead>
              <tbody>
                {tableRows.map((row, i) => (
                  <tr key={i}>
                    {row.map((value, j) => (
                      <td key={j} className={typeof value === "number" ? "num" : undefined}>
                        {typeof value === "number" ? cell(value) : value}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </Card>
  );
}

export function PageHead({ eyebrow, title, children }: {
  eyebrow?: string; title: string; children?: ReactNode;
}) {
  useEffect(() => {
    document.title = `${title} · Painel do Classificador`;
  }, [title]);
  return (
    <header className="page-head">
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      <h1>{title}</h1>
      {children && <p>{children}</p>}
    </header>
  );
}

/** Tooltip global das sparklines, posicionado em coordenadas de viewport. */
export function useTooltip() {
  const [tip, setTip] = useState<{ text: string; x: number; y: number } | null>(null);
  const node = (
    <div id="tip" role="status" aria-live="polite" data-open={tip ? "true" : "false"}
         style={tip ? { left: Math.min(tip.x + 12, window.innerWidth - 190), top: tip.y - 6 }
                    : undefined}>
      {tip?.text}
    </div>
  );
  return { setTip, tipNode: node };
}
